import { SaveCancelWidget } from 'components';
import { SchemaActionTypes, SchemaContext } from 'contexts/SchemaContext';
import { CACHE_FIRST, ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import type { ISchema } from 'contracts/interfaces';
import type { ActionButtonProps } from 'contracts/interfaces/ActionButtonProps';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GetModelDocument, useGetModelQuery, useUpdateModelSchemaMutation } from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

export const StructureActionButtons: FC<ActionButtonProps> = ({ isEditable }) => {
  const ctx = useContext(SchemaContext);
  const { pathname } = useLocation();
  const params = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setIsSaved(false);
  }, [ctx?.state.schema]);

  const { data: structureData } = useGetModelQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    ...CACHE_FIRST
  });

  /* TODO: Handle the error from this operation. Ticket for the implementation:
     https://deitycommerce.atlassian.net/browse/DI-564 */
  const [updateModelSchema, { loading: isSaving }] = useUpdateModelSchemaMutation({
    refetchQueries: [
      {
        query: GetModelDocument,
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId
        }
      }
    ]
  });

  const saveStructure = () => {
    if (params?.entityId && ctx?.state) {
      updateModelSchema({
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId,
          schema: ctx.state.schema
        },
        notifyOnNetworkStatusChange: true
      }).then(() => setIsSaved(true));
    }
    ctx?.dispatch({
      type: SchemaActionTypes.SELECT_FIELD,
      payload: { path: null }
    });
  };

  const cancelChange = () => {
    if (structureData?.getModel.schema) {
      ctx?.dispatch({
        type: SchemaActionTypes.INIT_SCHEMA,
        payload: {
          schema: structureData.getModel.schema as ISchema,
          uuid: structureData.getModel.uuid,
          name: structureData?.getModel.name
        }
      });
      ctx?.dispatch({
        type: SchemaActionTypes.SELECT_FIELD,
        payload: { path: null }
      });
    }
  };

  /**
   * Switched to JSON Stringify over !isEqual(structureData?.getModel.schema, ctx?.state.schema)
   * because changes in parameter order where not detected
   */
  // const renderCondition =
  //   !isSaved && structureData?.getModel.schema && !isEqual(structureData?.getModel.schema, ctx?.state.schema);

  const renderCondition =
    !isSaved &&
    structureData?.getModel.schema &&
    JSON.stringify(structureData?.getModel.schema) !== JSON.stringify(ctx?.state.schema);

  return (isSaving || renderCondition) && isEditable ? (
    <SaveCancelWidget onCancel={cancelChange} onSave={saveStructure} isSaving={isSaving} />
  ) : null;
};
